// deployment_class: customer_env_allowed
/**
 * New-project client — the composition catalog, and creating the project.
 *
 * The catalog is SERVED rather than compiled into this bundle, so a new module
 * or option reaches a customer without a frontend build.
 */
import { apiFetch } from '@v3-shell/api/api-client-base';

export type ProjectTrack = 'workflow' | 'development' | 'acquisition';

/** `available` does something today. `planned` is designed but not built. */
export type OptionStatus = 'available' | 'planned';

export interface CatalogOption {
  key: string;
  label: string;
  description: string;
  status: OptionStatus;
  type:
    | { kind: 'toggle'; default: boolean }
    | { kind: 'choice'; choices: string[]; default: string }
    | { kind: 'number'; default: number; unit: string };
  grounding: string;
}

export interface CatalogModule {
  key: string;
  label: string;
  summary: string;
  status: OptionStatus;
  always_on: boolean;
  options: CatalogOption[];
}

export interface Catalog {
  track: ProjectTrack;
  modules: CatalogModule[];
  default_selection: string[];
}

export function getCatalog(track: ProjectTrack, signal?: AbortSignal): Promise<Catalog> {
  return apiFetch(
    `/v3/projects/configuration/catalog?track=${encodeURIComponent(track)}`,
    signal ? { signal } : undefined,
  );
}

export interface CreatedProject {
  id: string;
  name: string;
  track: ProjectTrack;
}

/** A stored option value, per its declared kind. */
export type OptionValue = boolean | string | number;

export function createProject(input: {
  name: string;
  /**
   * Optional since 2026-10-04. The import screen stopped asking — the dashboard
   * as a whole IS development and acquisition — so an omitted track takes the
   * column default (migration 0180). The Projects module still sets it.
   */
  track?: ProjectTrack;
  /**
   * Only the options the customer TOUCHED. The server resolves everything else
   * to its catalog default and records it as `default`, which is what later
   * lets a new default be offered without overwriting a decision somebody made.
   */
  options?: Record<string, OptionValue>;
}): Promise<CreatedProject> {
  return apiFetch('/v3/projects', { method: 'POST', body: JSON.stringify(input) });
}

export interface SavedOption {
  module_key: string;
  option_key: string;
  value_kind: 'toggle' | 'choice' | 'number';
  value: OptionValue;
  source: 'user' | 'default';
  catalog_version: string;
}

export interface ProjectConfiguration {
  project_id: string;
  catalog_version: string | null;
  options: SavedOption[];
}

/** The configuration a project was created with. */
export function getProjectConfiguration(
  projectId: string,
  signal?: AbortSignal,
): Promise<ProjectConfiguration> {
  return apiFetch(
    `/v3/projects/${encodeURIComponent(projectId)}/configuration`,
    signal ? { signal } : undefined,
  );
}
